<template>
  <div class="page-container">
    <div class="page-title-container">
      <h1 class="page-title">{{ editing ? 'Editar lista de precios' : 'Nueva lista de precios' }}</h1>
    </div>
    <div>
      <form class="custom-form" @submit.prevent="save" novalidate>
        <b-card tag="article" no-body style="max-width: 760px" class="custom-card">
          <b-card-header header-tag="section" class="custom-card-header d-flex align-items-center">
            <h3>{{ editing ? 'Datos de la lista' : 'Datos de la nueva lista' }}</h3>
          </b-card-header>

          <b-card-body class="custom-card-body p-0">
            <section class="form-section">
              <div>
                <h2 class="form-subtitle">Información general</h2>
              </div>

              <div class="row">
                <!-- Nombre -->
                <div class="col-md-6">
                  <div class="form-group">
                    <label for="name">Nombre *</label>
                    <input id="name" type="text" v-model.trim="form.name" autocomplete="off"
                      placeholder="Ej: Colegio Markham" class="custom-form-control"
                      :class="{ 'custom-form-control-error': $v.form.name.$error }" />
                    <FormError v-if="$v.form.name.$error && !$v.form.name.required"
                      message="El nombre es obligatorio" />
                  </div>
                </div>

                <!-- Código -->
                <div class="col-md-6">
                  <div class="form-group">
                    <label for="code">Código *</label>
                    <input id="code" type="text" v-model.trim="form.code" autocomplete="off"
                      placeholder="Ej: colegio_markham" class="custom-form-control"
                      :class="{ 'custom-form-control-error': $v.form.code.$error }"
                      :disabled="editing" />
                    <FormError v-if="$v.form.code.$error && !$v.form.code.required"
                      message="El código es obligatorio" />
                    <small v-if="!editing" class="form-text text-muted">
                      Minúsculas, números y guión bajo. Ej: <code>hotel_marriott</code>
                    </small>
                  </div>
                </div>

                <!-- Tipo de cálculo -->
                <div class="col-md-6">
                  <div class="form-group">
                    <label for="calculation_type">Tipo de cálculo *</label>
                    <FormSelect id="calculation_type" v-model="form.calculation_type"
                      :items="calculationTypes"
                      defaultOption="Elegir tipo"
                      :showError="$v.form.calculation_type.$error"
                      :disabled="editing" />
                    <FormError v-if="$v.form.calculation_type.$error && !$v.form.calculation_type.required"
                      message="El tipo de cálculo es obligatorio" />
                  </div>
                </div>

                <!-- Descripción -->
                <div class="col-md-6">
                  <div class="form-group">
                    <label for="description">Descripción</label>
                    <input id="description" type="text" v-model.trim="form.description" autocomplete="off"
                      placeholder="Descripción opcional" class="custom-form-control" />
                  </div>
                </div>

                <!-- Fórmula — solo si dynamic -->
                <div class="col-md-12" v-if="isDynamic">
                  <div class="form-group">
                    <label for="formula">Fórmula *</label>
                    <input id="formula" type="text" v-model.trim="form.formula" autocomplete="off"
                      placeholder="Ej: CEILING(base * 1.5, 10)" class="custom-form-control"
                      :class="{ 'custom-form-control-error': $v.form.formula.$error }"
                      @input="onFormulaInput" />
                    <FormError v-if="$v.form.formula.$error && !$v.form.formula.required"
                      message="La fórmula es obligatoria para tipos dinámicos" />
                    <small class="form-text text-muted">
                      Use la variable <code>base</code> para referirse al precio base. Ej: <code>base * 1.5</code>
                    </small>
                  </div>
                </div>

                <!-- Simulador — solo si dynamic -->
                <div class="col-md-12" v-if="isDynamic">
                  <div class="form-group">
                    <label>Simulador</label>
                    <div class="simulator-box">
                      <div class="d-flex align-items-center">
                        <span class="simulator-label">Costo base</span>
                        <input type="number" v-model="baseInput" min="0.01" step="0.01"
                          placeholder="0.00" class="custom-form-control simulator-input"
                          @input="onBaseInput" />
                      </div>
                      <div class="simulator-result mt-2">
                        <span class="simulator-label">Resultado</span>
                        <span v-if="simulateResult !== null" class="simulator-value">
                          S/ {{ parseFloat(simulateResult).toFixed(2) }}
                        </span>
                        <span v-else-if="simulateError" class="simulator-error">{{ simulateError }}</span>
                        <span v-else class="text-muted">—</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="row">
                <div class="col-md-12">
                  <div class="form-group-buttons text-right">
                    <router-link :to="{ name: 'tarifas-price-type-list' }" class="button button-light">
                      {{ editing ? 'Volver' : 'Cancelar' }}
                    </router-link>
                    <button class="button button-primary" type="submit">Guardar</button>
                  </div>
                </div>
              </div>
            </section>
          </b-card-body>
        </b-card>
      </form>
    </div>
  </div>
</template>

<script>
import { Alert, FormSelect, FormError } from 'wize-admin'
import { required, requiredIf } from 'vuelidate/lib/validators'
import { PriceTypeService } from '@/core/services'

export default {
  components: { FormError, FormSelect },
  data() {
    return {
      editing: false,
      calculationTypes: [
        { key: 'dynamic', label: 'Dinámico (fórmula)' },
        { key: 'fixed', label: 'Fijo (tabla de precios)' },
      ],
      form: {
        name: null,
        code: null,
        calculation_type: null,
        description: null,
        formula: null,
      },
      baseInput: '',
      simulateResult: null,
      simulateError: null,
      simulateTimer: null,
    }
  },
  validations: {
    form: {
      name: { required },
      code: { required },
      calculation_type: { required },
      formula: {
        required: requiredIf('isDynamic'),
      },
    },
  },
  computed: {
    isDynamic() {
      return this.form.calculation_type === 'dynamic'
    },
  },
  watch: {
    isDynamic(val) {
      if (!val) {
        this.form.formula = null
        this.simulateResult = null
        this.simulateError = null
      }
    },
  },
  methods: {
    // Genera un code a partir del nombre: minúsculas, sin acentos, guión bajo.
    slugify(text) {
      return String(text || '')
        .toLowerCase()
        .normalize('NFD').replace(/[̀-ͯ]/g, '') // quita diacríticos
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
    },
    onFormulaInput() {
      this.scheduleSimulate()
    },
    onBaseInput() {
      this.scheduleSimulate()
    },
    scheduleSimulate() {
      if (this.simulateTimer) clearTimeout(this.simulateTimer)
      this.simulateTimer = setTimeout(() => this.runSimulate(), 400)
    },
    async runSimulate() {
      const formula = this.form.formula
      const amount = parseFloat(this.baseInput)
      if (!formula || isNaN(amount) || amount <= 0) {
        this.simulateResult = null
        this.simulateError = null
        return
      }
      try {
        const res = await PriceTypeService.simulate(formula, amount)
        this.simulateResult = res.payload.result
        this.simulateError = null
      } catch {
        this.simulateResult = null
        this.simulateError = 'Fórmula inválida'
      }
    },
    async save() {
      this.$v.$touch()
      if (this.$v.$error) {
        this.$nextTick(() => {
          this.$smoothScroll({
            scrollTo: document.querySelector('.form-error'),
            updateHistory: false,
            offset: -50,
          })
        })
        return
      }

      try {
        this.$store.dispatch('app/loading', true)

        if (!this.editing) {
          const data = {
            name: this.form.name,
            code: this.form.code,
            calculation_type: this.form.calculation_type,
            description: this.form.description || null,
            formula: this.isDynamic ? this.form.formula : null,
          }
          await PriceTypeService.create(data)
          Alert.success('Lista creada correctamente')
          this.$router.push({ name: 'tarifas-price-type-list' })
        } else {
          const data = {
            name: this.form.name,
            description: this.form.description || null,
            formula: this.isDynamic ? this.form.formula : null,
          }
          await PriceTypeService.update(this.$route.params.id, data)
          Alert.success('Lista actualizada correctamente')
          await this.load(this.$route.params.id)
        }
      } catch (ex) {
        console.error(ex)
        Alert.error(ex.errors?.message || 'Error al guardar la lista')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
    async load(id) {
      try {
        this.$store.dispatch('app/loading', true)
        const res = await PriceTypeService.list()
        const pt = (res.payload || []).find(p => p.id === parseInt(id))
        if (!pt) {
          Alert.error('Lista no encontrada')
          this.$router.push({ name: 'tarifas-price-type-list' })
          return
        }
        this.form = {
          name: pt.name,
          code: pt.code,
          calculation_type: pt.calculation_type,
          description: pt.description || null,
          formula: pt.formula || null,
        }
        this.editing = true
      } catch (e) {
        console.error(e)
        Alert.error(e?.errors?.message || 'Error al cargar la lista')
        this.$router.push({ name: 'tarifas-price-type-list' })
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
  },
  async created() {
    if (this.$route.params.id) {
      await this.load(this.$route.params.id)
    } else if (this.$route.query.name) {
      // Prefill al crear desde un enlace con ?name= (DT-015).
      this.form.name = this.$route.query.name
      this.form.code = this.slugify(this.$route.query.name)
    }
  },
}
</script>

<style lang="stylus" scoped>
.simulator-box
  background-color #f9fafb
  border 1px solid #e5e7eb
  border-radius 6px
  padding 12px

.simulator-label
  font-size 0.8rem
  color #6b7280
  min-width 90px
  display inline-block

.simulator-input
  max-width 130px
  display inline-block

.simulator-result
  display flex
  align-items center

.simulator-value
  font-size 1.1rem
  font-weight 600
  color #16a34a

.simulator-error
  font-size 0.85rem
  color #dc2626
</style>
